import { useState, useCallback } from 'react';
import { useServices } from 'services';
import useLoadingStateManager from './useLoadingStateManager';
import { LoadingStatus } from 'types/LoadingStateManager';
import User from 'types/User';
import { MembershipTier } from 'types/SignUpData';
import { toBackendTier } from 'utils/tierMapping';

interface UseUserMembershipsReturn {
  users: User[];
  isLoading: boolean;
  persistedCommentByEmail: Record<string, string>;
  fetchAllUsers: () => Promise<void>;
  adminUpdateMembershipTier: (
    user: User,
    tier: MembershipTier,
    comment?: string
  ) => Promise<void>;
  postAdminComment: (user: User, comment: string) => Promise<void>;
  enableUserAccess: (user: User, comment?: string) => Promise<void>;
  restrictUserAccess: (user: User, comment?: string) => Promise<void>;
}

export default function useUserMemberships(): UseUserMembershipsReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [persistedCommentByEmail, setPersistedCommentByEmail] = useState<Record<string, string>>({});
  const { authService } = useServices();
  const loadingStateManager = useLoadingStateManager();
  const isLoading = loadingStateManager.loadingState.status === LoadingStatus.LOADING;

  const fetchAllUsers = useCallback(async (): Promise<void> => {
    try {
      const [allUsers, comments] = await Promise.all([
        authService.fetchAllUsers(),
        authService.fetchLatestAdminComments(),
      ]);
      setUsers(allUsers);
      setPersistedCommentByEmail(comments);
    } catch (error) {
      console.error('Error fetching all users:', error);
    }
  }, [authService]);

  // Admin updates a user membership tier outside of a user membership upgrade request
  const adminUpdateMembershipTier = useCallback(
    async (
      user: User,
      tier: MembershipTier,
      comment?: string
    ): Promise<void> => {
      const doUpdate = async (): Promise<void> => {
        await authService.adminUpdateMembershipTier(user, toBackendTier(tier), comment);
        user.membershipTier = tier;
        await fetchAllUsers();
      };
      await loadingStateManager.startLoading({ waitOn: doUpdate() });
    },
    [authService, fetchAllUsers, loadingStateManager]
  );

  const postAdminComment = useCallback(
    async (user: User, comment: string): Promise<void> => {
      const doPost = async (): Promise<void> => {
        await authService.postAdminComment(user, comment);
      };
      await loadingStateManager.startLoading({ waitOn: doPost() });
    },
    [authService, loadingStateManager]
  );

  const enableUserAccess = useCallback(
    async (user: User, comment?: string): Promise<void> => {
      const doEnable = async (): Promise<void> => {
        await authService.enableUserAccess(user, comment);
        user.approved = true;
        user.requestStatus = 'ACCOUNT_ENABLED';
        await fetchAllUsers();
      };
      await loadingStateManager.startLoading({ waitOn: doEnable() });
    },
    [authService, fetchAllUsers, loadingStateManager]
  );

  const restrictUserAccess = useCallback(
    async (user: User, comment?: string): Promise<void> => {
      const doRestrict = async (): Promise<void> => {
        await authService.restrictUserAccess(user, comment);
        user.approved = false;
        user.requestStatus = 'ACCOUNT_DISABLED';
        await fetchAllUsers();
      };
      await loadingStateManager.startLoading({ waitOn: doRestrict() });
    },
    [authService, fetchAllUsers, loadingStateManager]
  );

  return {
    users,
    isLoading,
    persistedCommentByEmail,
    fetchAllUsers,
    adminUpdateMembershipTier,
    postAdminComment,
    enableUserAccess,
    restrictUserAccess,
  };
}